import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, tap } from 'rxjs/operators';
import { ApiErrorService, tapAction } from './api-messaging/api-error.service';
import { ApiMessagingService } from './api-messaging/api-messaging.service';

export type ErrorOptions = { duration?: number };
export type InfoOptions = { duration?: number };

const DefaultErrorOptions: ErrorOptions = { duration: 10000 };
const DefaultInfoOptions: InfoOptions = { duration: 3000 };

@Injectable()
export class SnackbarService {
  constructor(
    private snackbar: MatSnackBar,
    private apiMessageService: ApiMessagingService,
    private apiErrorService: ApiErrorService
  ) {}

  error(obj: string | Error, options?: ErrorOptions) {
    const message = typeof obj === 'string' ? obj : obj?.message;
    if (!message) return;

    options = { ...DefaultErrorOptions, ...options };
    this.snackbar.open(message, $localize`Close`, { duration: options.duration });
  }

  info(message: string, options?: InfoOptions) {
    if (!message) return;

    options = { ...DefaultInfoOptions, ...options };
    this.snackbar.open(message, undefined, { duration: options.duration });
  }

  displaySuccess<T>(action: string, options?: InfoOptions) {
    return tapAction<T>(
      tap(() => {
        this.info(this.apiMessageService.message(action), options);
      })
    );
  }

  displayApiError<T>(record?: object) {
    return tapAction<T>(this.apiErrorService.convertApiError(record), this.displayErrorMessage());
  }

  displayErrorMessage<T>() {
    return tapAction<T>(
      catchError((error: any) => {
        this.error(error);
        throw error;
      })
    );
  }

  ensureFound<T>(record: { id: number } & any) {
    return tapAction<T>(this.apiErrorService.ensureFound(record), this.displayApiError());
  }

  displayApiResponse<T>(action: string, record: object, options?: InfoOptions) {
    return tapAction<T>(
      this.apiErrorService.convertApiError(record),
      this.apiErrorService.ensureMutationSuccess(),
      this.displayErrorMessage(),
      this.displaySuccess(action, options)
    );
  }
}
